import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Autorenew as AutorenewIcon
} from '@mui/icons-material';

const UpcomingServicesTable = ({
  services = [],
  loading = false,
  error = null,
  onEarlyRenew,
  renewingId = null
}) => {
  const [confirmService, setConfirmService] = useState(null);

  const getStatusColor = (daysUntilExpiry) => {
    if (daysUntilExpiry <= 0) return 'error';
    if (daysUntilExpiry <= 3) return 'warning';
    if (daysUntilExpiry <= 7) return 'info';
    return 'success';
  };

  const getStatusIcon = (daysUntilExpiry) => {
    if (daysUntilExpiry <= 0) return <WarningIcon />;
    if (daysUntilExpiry <= 3) return <ScheduleIcon />;
    return <CheckCircleIcon />;
  };

  const getStatusText = (daysUntilExpiry) => {
    if (daysUntilExpiry <= 0) return 'Λήγει σήμερα';
    if (daysUntilExpiry === 1) return 'Λήγει αύριο';
    if (daysUntilExpiry <= 7) return `Λήγει σε ${daysUntilExpiry} ημέρες`;
    return `Λήγει σε ${daysUntilExpiry} ημέρες`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('el-GR');
  };

  const scheduledStartLabel = (days) => {
    if (days == null) return '—';
    if (days <= 0) return 'Εκκρεμεί ενεργοποίηση / τιμολόγηση';
    if (days === 1) return 'Έναρξη περιόδου αύριο';
    return `Έναρξη περιόδου σε ${days} ημέρες`;
  };

  const scheduledStartColor = (days) => {
    if (days == null) return 'default';
    if (days <= 0) return 'warning';
    if (days <= 7) return 'warning';
    return 'info';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (services.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Δεν υπάρχουν επερχόμενες υπηρεσίες
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Dialog open={Boolean(confirmService)} onClose={() => setConfirmService(null)}>
        <DialogTitle>Πρόωρη ανανέωση</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Θα δημιουργηθεί η επόμενη περίοδος ως <strong>εκκρεμής (PENDING)</strong> για τιμολόγηση· η τρέχουσα περίοδος
            παραμένει <strong>ενεργή</strong> μέχρι την ημερομηνία λήξης της. Όταν φτάσει η ημερομηνία έναρξης της νέας
            περιόδου, το σύστημα θα ενεργοποιήσει αυτόματα τη νέα υπηρεσία (μέσω scheduler).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmService(null)}>Ακύρωση</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const s = confirmService;
              setConfirmService(null);
              if (s && onEarlyRenew) await onEarlyRenew(s);
            }}
          >
            Επιβεβαίωση
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Επερχόμενες Υπηρεσίες ({services.length})
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ρίζα</TableCell>
              <TableCell>Υπηρεσία</TableCell>
              <TableCell>Πελάτης</TableCell>
              <TableCell>Πρόγραμμα</TableCell>
              <TableCell>Τύπος</TableCell>
              <TableCell>Τιμή</TableCell>
              <TableCell>Ημερομηνίες περιόδου</TableCell>
              <TableCell>Κατάσταση</TableCell>
              <TableCell>Αυτόματη Ανανέωση</TableCell>
              <TableCell align="right">Ενέργειες</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((service) => {
              const isScheduled = service.row_kind === 'scheduled_renewal';
              return (
                <TableRow key={`${service.row_kind || 'exp'}-${service.id}`} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={isScheduled ? 'Νέα περίοδος' : 'Τρέχουσα λήξη'}
                      color={isScheduled ? 'secondary' : 'primary'}
                      variant={isScheduled ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {service.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {service.client_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {service.project_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={service.service_type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatPrice(service.price)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="div">
                      {isScheduled && service.start_date && (
                        <span>Έναρξη: {formatDate(service.start_date)}<br /></span>
                      )}
                      {service.end_date && (
                        <span>Λήξη: {formatDate(service.end_date)}</span>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {isScheduled ? (
                      <Chip
                        label={scheduledStartLabel(service.days_until_period_start)}
                        color={scheduledStartColor(service.days_until_period_start)}
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={getStatusIcon(service.days_until_expiry)}
                        label={getStatusText(service.days_until_expiry)}
                        color={getStatusColor(service.days_until_expiry)}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={service.auto_renew ? 'Ναι' : 'Όχι'}
                      color={service.auto_renew ? 'success' : 'default'}
                      size="small"
                      variant={service.auto_renew ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {service.can_early_renew && onEarlyRenew && (
                      <Tooltip title="Πρόωρη δημιουργία επόμενης περιόδου (για τιμολόγιο)">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={renewingId === service.id}
                            onClick={() => setConfirmService(service)}
                          >
                            {renewingId === service.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <AutorenewIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UpcomingServicesTable;
